import type { CreateCarouselBannerInput } from "src/features/HomepageCarouselBanners/models";
import type { HashtagModel } from "src/features/Hashtags/models";

import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	FormProvider,
	useController,
	useForm,
	useFormState
} from "react-hook-form";
import { BannerTemplatePicker } from "src/features/HomepageCarouselBanners/components/BannerTemplatePicker";
import { CreateCarouselBannerSchema } from "src/features/HomepageCarouselBanners/models";
import { ImageCarouselBannerFormStepList } from "src/features/HomepageCarouselBanners/containers/ImageCarouselBannerFormStepList";
import { VideoCarouselBannerFormStepList } from "src/features/HomepageCarouselBanners/containers/VideoCarouselBannerFormStepList";
import { TextCarouselBannerFormStepList } from "src/features/HomepageCarouselBanners/containers/TextCarouselBannerFormStepList";
import ImageCarouselBanner from "src/features/HomepageCarouselBanners/components/ImageCarouselBanner/ImageCarouselBanner";
import TextCarouselBanner from "src/features/HomepageCarouselBanners/components/TextCarouselBanner/TextCarouselBanner";
import VideoCarouselBanner from "src/features/HomepageCarouselBanners/components/VideoCarouselBanner/VideoCarouselBanner";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Save } from "lucide-react";
import { Button } from "src/components/ui/button";
import isEmpty from "lodash.isempty";
import GroupsAudienceCard from "src/components/GroupsAudienceCard/GroupsAudienceCard";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";

export type CreateCarouselBannerValidationSchema = CreateCarouselBannerInput & {
	id?: number;
	selectedFeed?: HashtagModel;
};

export interface CreateCarouselBannerFormProps {
	defaultValues?: Partial<CreateCarouselBannerValidationSchema>;
	onValid: (data: CreateCarouselBannerValidationSchema) => void;
}

const CreateCarouselBannerForm = ({
	defaultValues,
	onValid
}: CreateCarouselBannerFormProps) => {
	const resolver = zodResolver(CreateCarouselBannerSchema);

	const methods = useForm<CreateCarouselBannerValidationSchema>({
		mode: "onChange",
		reValidateMode: "onChange",
		resolver,
		defaultValues
	});

	const { control, reset, handleSubmit, watch } = methods;
	const { errors, isValid } = useFormState({ control });

	const template = watch("template");
	const videoUuid = watch("videoUuid");
	const imageUuid = watch("imageUuid");
	const headingText = watch("headingText");
	const subheadingText = watch("subheadingText");
	const backgroundColor = watch("backgroundColor");
	const foregroundColor = watch("foregroundColor");

	const { field: feed } = useController({ control, name: "feed" });
	const { field: selectedFeed } = useController({
		control,
		name: "selectedFeed"
	});
	const { field: audienceGroupIds } = useController({
		control,
		name: "audienceGroupIds",
		defaultValue: []
	});
	const {
		data: adminGroups = [],
		isLoading: groupsLoading,
		isError: groupsError
	} = useAdminGroups();

	// Mirror audienceGroupIds in a ref so the template-change effect can read
	// the latest selection without re-running on every keystroke. Tested in
	// CreateCarouselBannerForm.spec.tsx.
	const audienceGroupIdsRef = useRef<number[]>(audienceGroupIds.value ?? []);
	useEffect(() => {
		audienceGroupIdsRef.current = audienceGroupIds.value ?? [];
	});

	useEffect(() => {
		if (template === defaultValues?.template) {
			reset(defaultValues);
			return;
		}

		const preservedGroupIds = audienceGroupIdsRef.current;

		if (template === "fullImageBanner") {
			reset({
				template: "fullImageBanner",
				audienceGroupIds: preservedGroupIds
			});
		}

		if (template === "videoBanner") {
			reset({
				template: "videoBanner",
				audienceGroupIds: preservedGroupIds
			});
		}

		if (template === "textBanner") {
			reset({
				template: "textBanner",
				backgroundColor: "#000000",
				foregroundColor: "#ffffff",
				audienceGroupIds: preservedGroupIds
			});
		}
	}, [template]);

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	const isSubmitBtnDisabled = !isEmpty(errors) && !isValid;

	const renderPreview = () => {
		if (template === "fullImageBanner") {
			return (
				<ImageCarouselBanner
					imageUuid={imageUuid}
					headingText={headingText}
					subheadingText={subheadingText}
					showGradient={true}
				/>
			);
		}

		if (template === "videoBanner") {
			return (
				<VideoCarouselBanner
					videoUuid={videoUuid}
					headingText={headingText}
					subheadingText={subheadingText}
				/>
			);
		}

		if (template === "textBanner") {
			return (
				<TextCarouselBanner
					backgroundColor={backgroundColor}
					foregroundColor={foregroundColor}
					headingText={headingText}
					subheadingText={subheadingText}
				/>
			);
		}

		return null;
	};

	return (
		<form onSubmit={handleSubmit(onValid)}>
			<FormProvider {...methods}>
				<div className="pt-6">
					<BannerTemplatePicker
						name="template"
						defaultValue="fullImageBanner"
					/>

					{/* Form fields */}
					<div>
						{template === "fullImageBanner" && (
							<ImageCarouselBannerFormStepList
								selectedImage={imageUuid}
								errors={errors}
							/>
						)}

						{template === "videoBanner" && (
							<VideoCarouselBannerFormStepList
								selectedVideo={videoUuid}
								errors={errors}
							/>
						)}

						{template === "textBanner" && (
							<TextCarouselBannerFormStepList errors={errors} />
						)}
					</div>

					{/* Preview */}
					<div className="mt-8 p-5 bg-primary-lighter rounded-xl border border-primary-light/30">
						<div className="flex items-center gap-2 mb-3 text-xs font-medium uppercase tracking-wider text-primary-dark">
							<Eye className="h-3.5 w-3.5" />
							<span>Preview</span>
						</div>
						<div className="rounded-2xl overflow-hidden shadow-md max-w-[480px]">
							{renderPreview()}
						</div>
					</div>

					{/* Groups audience */}
					<div className="mt-8">
						<GroupsAudienceCard
							groupIds={audienceGroupIds.value ?? []}
							onChange={audienceGroupIds.onChange}
							groups={adminGroups.map((g) => ({
								id: Number(g.id),
								name: g.name
							}))}
							isLoading={groupsLoading}
							isError={groupsError}
						/>
					</div>

					{/* Footer */}
					<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
						<Link
							to="/vendor-sponsorship/homepage-carousel/banners?published=true"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to carousel
						</Link>

						<Button type="submit" disabled={isSubmitBtnDisabled}>
							<Save className="h-4 w-4" />
							Save banner
						</Button>
					</div>
				</div>
			</FormProvider>
		</form>
	);
};

export default CreateCarouselBannerForm;
